import { Box, Heading, Paragraph } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';

import numberUtils from 'app/shared/utils/numberUtils';
import DataCard from 'app/shared/components/DataCard';

import type React from 'react';
import type { ReactElement } from 'react';
import type { CardRootPropsInterface } from '@zillow/constellation-10';

const hideOnSmallScreen = css({ _smallScreen: { display: 'none' } });
const hideAboveSmallScreen = css({ _aboveSmallScreen: { display: 'none' } });

interface MarketSummaryProps {
  heading?: string | ReactElement;
  market: string;
  filterDescription: string;
  medianRent: number | null;
  monthlyChange: number | null;
  yearlyChange: number | null;
  avgDaysOnMarket: number | null;
  availableRentals: number | null;
}

const MarketSummary: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<MarketSummaryProps & CardRootPropsInterface>>
> = (props) => {
  const {
    heading,
    market,
    filterDescription,
    medianRent,
    monthlyChange,
    yearlyChange,
    avgDaysOnMarket,
    availableRentals,
    elevated,
    outlined,
  } = props;

  const statContainerWidth = { base: '200px', _smallScreen: '135px' };

  return (
    <DataCard
      heading={heading ? heading : `Rental market summary`}
      copyText={
        medianRent ? (
          <>
            The average rent for {filterDescription} in {market} is{' '}
            <strong>{numberUtils.asCurrency(medianRent)}.</strong>
          </>
        ) : (
          ''
        )
      }
      source="Zillow Rentals data"
      useFullScreen={true}
      elevated={elevated}
      outlined={outlined}
    >
      <Box
        css={{
          display: 'flex',
          flexWrap: 'wrap',
          marginBottom: '1200',
          marginInline: { _aboveSmallScreen: '600' },
          justifyContent: 'space-between',
          gap: '600',
        }}
      >
        {medianRent !== null && (
          <Box css={{ width: statContainerWidth }}>
            <Paragraph textStyle="body-sm" css={{ whiteSpace: 'nowrap' }}>
              Average rent
            </Paragraph>
            <Heading level={2} textStyle="responsive.heading-lg" fontFamily="heading-alt">
              {numberUtils.asCurrency(medianRent)}
            </Heading>
          </Box>
        )}
        {monthlyChange !== null && (
          <Box css={{ width: statContainerWidth }}>
            <Paragraph textStyle="body-sm" css={{ whiteSpace: 'nowrap' }}>
              <span className={hideOnSmallScreen}>Month-over-month</span>
              <span className={hideAboveSmallScreen}>MoM</span> change
            </Paragraph>
            <Heading level={2} textStyle="responsive.heading-lg" fontFamily="heading-alt">
              {monthlyChange > 0 && '+'}
              {numberUtils.asCurrency(monthlyChange)}
            </Heading>
          </Box>
        )}
        {yearlyChange !== null && (
          <Box css={{ width: statContainerWidth }}>
            <Paragraph textStyle="body-sm" css={{ whiteSpace: 'nowrap' }}>
              <span className={hideOnSmallScreen}>Year-over-year</span>
              <span className={hideAboveSmallScreen}>YoY</span> change
            </Paragraph>
            <Heading level={2} textStyle="responsive.heading-lg" fontFamily="heading-alt">
              {yearlyChange > 0 && '+'}
              {numberUtils.asCurrency(yearlyChange)}
            </Heading>
          </Box>
        )}
        {avgDaysOnMarket !== null && (
          <Box css={{ width: statContainerWidth }}>
            <Paragraph textStyle="body-sm" css={{ whiteSpace: 'nowrap' }}>
              Avg. Days On Market
            </Paragraph>
            <Heading level={2} textStyle="responsive.heading-lg" fontFamily="heading-alt">
              {numberUtils.fullNumber(Math.round(avgDaysOnMarket))}
            </Heading>
          </Box>
        )}
        {availableRentals !== null && (
          <Box css={{ width: statContainerWidth }}>
            <Paragraph textStyle="body-sm" css={{ whiteSpace: 'nowrap' }}>
              Available rentals
            </Paragraph>
            <Heading level={2} textStyle="responsive.heading-lg" fontFamily="heading-alt">
              {numberUtils.fullNumber(availableRentals)}
            </Heading>
          </Box>
        )}
      </Box>
    </DataCard>
  );
};

export default MarketSummary;
